import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";

type SubMenuType = "reply" | "more";

interface IContextProps {
  onMessageEnter: (messageId: string) => () => void;
  onMessageLeave: (messageId: string) => () => void;
  onFileEnter: (fileId: string) => () => void;
  onFileLeave: (fileId: string) => () => void;
  isMessageMenuOpen: (messageId: string) => boolean;
  isFileMenuOpen: (fileId: string) => boolean;
  onSubmenuOpen: (id: string, type: SubMenuType) => () => void;
  onSubmenuClose: (id: string, type: SubMenuType) => () => void;
  isSubMenuOpen: (id: string, type: SubMenuType) => boolean;
}

const MessageHoverContext = createContext<IContextProps>({
  onMessageEnter: () => () => {},
  onMessageLeave: () => () => {},
  onFileEnter: () => () => {},
  onFileLeave: () => () => {},
  isMessageMenuOpen: () => false,
  isFileMenuOpen: () => false,
  onSubmenuOpen: () => () => {},
  onSubmenuClose: () => () => {},
  isSubMenuOpen: () => false,
});

export interface IProviderProps {
  children?: ReactNode;
}

export const MessageHoverProvider = ({ children }: IProviderProps) => {
  const [messageId, setMessageId] = useState<string>();
  const [fileId, setFileId] = useState<string>();
  const [subMenuId, setSubMenuId] = useState<string>();
  const [subMenuType, setSubMenuType] = useState<SubMenuType>();

  const onMessageEnter = useCallback(
    (messageId: string) => () => {
      setMessageId(messageId);
    },
    [],
  );

  const onMessageLeave = useCallback(
    (messageId: string) => () => {
      if (subMenuId === messageId) return;
      setMessageId((prev) => (prev === messageId ? undefined : prev));
      setSubMenuId((prev) => (prev === messageId ? undefined : prev));
    },
    [subMenuId],
  );

  const onFileEnter = useCallback(
    (fileId: string) => () => {
      setFileId(fileId);
    },
    [],
  );

  const onFileLeave = useCallback(
    (fileId: string) => () => {
      if (subMenuId === fileId) return;
      setFileId((prev) => (prev === fileId ? undefined : prev));
      setSubMenuId((prev) => (prev === fileId ? undefined : prev));
    },
    [subMenuId],
  );

  const isMessageMenuOpen = useCallback(
    (_messageId: string) =>
      (messageId === _messageId || subMenuId === _messageId) && !fileId,
    [messageId, fileId, subMenuId],
  );

  const isFileMenuOpen = useCallback(
    (_fileId: string) => fileId === _fileId || subMenuId === _fileId,
    [fileId, subMenuId],
  );

  const onSubmenuOpen = useCallback(
    (id: string, type: SubMenuType) => () => {
      setSubMenuId(id);
      setSubMenuType(type);
    },
    [],
  );

  const onSubmenuClose = useCallback(
    (id: string, type: SubMenuType) => () => {
      setSubMenuId((prev) => (prev === id ? undefined : prev));
      if (subMenuId === id) {
        setSubMenuType((prev) => (prev === type ? undefined : prev));
      }
    },
    [subMenuId],
  );

  const isSubMenuOpen = useCallback(
    (id: string, type: SubMenuType) => subMenuId === id && subMenuType === type,
    [subMenuId, subMenuType],
  );

  const value = useMemo<IContextProps>(
    () => ({
      onMessageEnter,
      onMessageLeave,
      onFileEnter,
      onFileLeave,
      isMessageMenuOpen,
      isFileMenuOpen,
      onSubmenuOpen,
      onSubmenuClose,
      isSubMenuOpen,
    }),
    [
      onMessageEnter,
      onMessageLeave,
      onFileEnter,
      onFileLeave,
      isMessageMenuOpen,
      isFileMenuOpen,
      onSubmenuOpen,
      onSubmenuClose,
      isSubMenuOpen,
    ],
  );

  return (
    <MessageHoverContext.Provider value={value}>
      {children}
    </MessageHoverContext.Provider>
  );
};

export const useMessageHover = () => useContext(MessageHoverContext);
